import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore } from '@/stores/createStore';

export type WorkbenchLayoutState = {
  /** Whether the user has locked the current workbench layout against panel resizing and movement. */
  layoutLocked: boolean;
};

export type WorkbenchLayoutDehydratedState = WorkbenchLayoutState;

export type WorkbenchLayoutActions = {
  setLayoutLocked: (locked: boolean) => void;
  reset: () => void;
};

const initialState: WorkbenchLayoutState = {
  layoutLocked: false,
};

export const dehydrateWorkbenchLayoutState = (state: WorkbenchLayoutState): WorkbenchLayoutDehydratedState => ({
  layoutLocked: state.layoutLocked,
});

const workbenchLayoutActionsFactory: ActionsFactory<WorkbenchLayoutState, WorkbenchLayoutActions> = (
  set,
  _get,
  resetState,
) => ({
  setLayoutLocked: (locked) =>
    set((draft) => {
      draft.layoutLocked = locked;
    }),

  reset: resetState,
});

const [WorkbenchLayoutStoreProvider, useWorkbenchLayoutStoreInstance, useWorkbenchLayoutSelector] = createStore(
  'WorkbenchLayout',
  initialState,
  workbenchLayoutActionsFactory,
);

export { WorkbenchLayoutStoreProvider };

export const useLayoutLocked = () => useWorkbenchLayoutSelector((state) => state.layoutLocked);
export const useWorkbenchLayoutActions = () => useWorkbenchLayoutStoreInstance().actions;

export const usePersistWorkbenchLayoutStore = () => {
  const register = useRegisterPersistedStore();
  const store = useWorkbenchLayoutStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqWorkbenchLayoutStore',
      persistenceLevel: 'WORKBENCH',
      store,
      dehydrate: dehydrateWorkbenchLayoutState,
    });
  }, [register, store]);
};
